<template>
  <div class="search-hit-list">
    <div v-if="!rows || rows.length === 0" class="search-hit-empty">No matching results</div>
    <div v-for="row in rows" :key="row.kind + '-' + row.id" class="search-hit">
      <div class="search-hit-icon" :style="{ color: iconFor(row).color }">
        <font-awesome-icon :icon="[iconFor(row).iconSet, iconFor(row).iconName]"></font-awesome-icon>
      </div>
      <div class="search-hit-body">
        <div class="search-hit-title-row">
          <router-link
            v-if="row.kind === 'case'"
            class="search-hit-title"
            :to="caseRoute(row)">{{ row.title }}</router-link>
          <a
            v-else
            href=""
            class="search-hit-title"
            @click.prevent="openFile(row)">{{ row.title || row.filename }}</a>
          <span v-if="row.kind === 'document'" class="search-hit-badge" :style="badgeStyle(badgeText(row))">{{ badgeText(row) }}</span>
        </div>
        <div v-if="lineFor(row)" class="search-hit-match">
          Matched in {{ lineFor(row).label }} — <span v-html="highlightHtml(lineFor(row).snippet, searchTerm)"></span>
        </div>
        <div class="search-hit-meta">
          <template v-if="row.kind === 'case'">
            <span v-for="(part, index) in caseMetaParts(row)" :key="row.id + '-meta-' + index">
              <span v-if="index > 0"> · </span>
              <v-tooltip bottom color="secondary">
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ part.text }}</span>
                </template>
                <span>{{ part.description }}</span>
              </v-tooltip>
            </span>
          </template>
          <template v-else>
            <span>On </span>
            <span v-for="(part, index) in documentMetaParts(row)" :key="row.id + '-doc-meta-' + index">
              <span v-if="part.separator">{{ part.separator }}</span>
              <v-tooltip bottom color="secondary">
                <template v-slot:activator="{ on }">
                  <router-link v-if="part.link && row.caseId" v-on="on" class="search-hit-meta-link" :to="caseRoute(row)">{{ part.text }}</router-link>
                  <span v-else v-on="on">{{ part.text }}</span>
                </template>
                <span>{{ part.description }}</span>
              </v-tooltip>
            </span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getIconForMimeType } from "@/lib/mimeTypeIcons.js"
import { badgeStyle, badgeText, fieldLabel, formatSize, highlightHtml, quotedTitle } from "@/components/search/unifiedSearch.js"

export default {
  name: "SearchHitList",
  props: {
    rows: {
      type: Array,
      default: function() { return [] }
    },
    searchTerm: {
      type: String,
      default: ""
    },
    formatDate: {
      type: Function,
      default: function() { return "" }
    }
  },
  methods: {
    badgeText: badgeText,
    badgeStyle: badgeStyle,
    highlightHtml: highlightHtml,
    formatSize: formatSize,
    iconFor(row) {
      if (row.kind === "document") {
        return getIconForMimeType(row.contentType, row.filename || row.title)
      }
      if (row.iconSet && row.iconCode) {
        return { iconSet: row.iconSet, iconName: row.iconCode, color: "#607d8b" }
      }
      return { iconSet: "far", iconName: "folder", color: "#607d8b" }
    },
    caseRoute(row) {
      return { name: "Case", params: { case_id: row.caseId || row.id } }
    },
    lineFor(row) {
      const label = row.matchedFieldLabel || fieldLabel(null, row.matchedField) || (row.kind === "document" ? "filename" : "title")
      const snippet = row.highlight || row.title || row.filename || ""
      if (!snippet) {
        return null
      }
      return { label: label, snippet: snippet }
    },
    caseMetaParts(row) {
      const parts = []
      if (row.caseTypeName) {
        parts.push({ text: String(row.caseTypeName), description: "Case template" })
      }
      if (row.currentState) {
        parts.push({ text: row.currentState, description: "Current state" })
      }
      const updated = this.formatDate(row.updatedAt)
      if (updated) {
        parts.push({ text: "updated " + updated, description: "Last updated" })
      }
      if (row.latestActivity) {
        parts.push({ text: row.latestActivity, description: "Latest activity" })
      }
      return parts
    },
    documentMetaParts(row) {
      const parts = []
      const typeName = row.caseTypeName ? String(row.caseTypeName) : "case"
      parts.push({ text: typeName, description: "Case template", link: true, separator: "" })
      const title = quotedTitle(row.caseTitle)
      if (title) {
        parts.push({ text: title, description: "Case title", link: true, separator: " " })
      }
      const size = this.formatSize(row.size)
      if (size) {
        parts.push({ text: size, description: "File size", link: false, separator: " · " })
      }
      const uploaded = this.formatDate(row.createdAt)
      if (uploaded) {
        parts.push({ text: "uploaded " + uploaded, description: "Uploaded", link: false, separator: " · " })
      }
      return parts
    },
    openFile(row) {
      this.$emit("open-file", row)
    }
  }
}
</script>

<style lang="scss" scoped>
.search-hit-empty {
  padding: 28px 0;
  color: rgba(0, 0, 0, 0.45);
}

.search-hit {
  display: flex;
  gap: 14px;
  padding: 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.search-hit-icon {
  flex: 0 0 20px;
  width: 20px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-top: 3px;
}

.search-hit-body {
  min-width: 0;
  flex: 1 1 auto;
}

.search-hit-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.search-hit-title {
  font-weight: 600;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-hit-badge {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
}

.search-hit-match {
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.62);
  font-size: 14px;
  line-height: 1.4;
}

.search-hit-match >>> .search-hit-mark {
  background: #fde68a;
  color: inherit;
  padding: 0 2px;
  border-radius: 2px;
}

.search-hit-meta {
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
  line-height: 1.4;
}

.search-hit-meta-link {
  color: inherit;
  text-decoration: none;
}
</style>
